import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Container,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import MemoryIcon from "@mui/icons-material/Memory";
import CloudIcon from "@mui/icons-material/Cloud";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";

import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import FloatingMegaMenu from "./FloatingMegaMenu";
import { useLocation } from "react-router-dom";

type Props = {
  bannerVisible?: boolean;
};

type MenuItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
  link?: string;
};

export default function Navbar({ bannerVisible }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  const closeTimer = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  const openMenu = (menu: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMenu(menu);
  };

  const closeMenu = () => {
    closeTimer.current = setTimeout(() => {
      setActiveMenu(null);
    }, 200);
  };

  /*
  -------------------------
  MENU DATA
  -------------------------
  */

  const menus: Record<string, MenuItem[]> = {
    solutions: [
      {
        icon: <SmartToyIcon fontSize="large" />,
        title: "AI Solutions",
        description: "Machine learning and AI powered automation",
      },

      {
        icon: <PrecisionManufacturingIcon fontSize="large" />,
        title: "Manufacturing",
        description: "Smart manufacturing and MES systems",
      },

      {
        icon: <MemoryIcon fontSize="large" />,
        title: "Automation",
        description: "Industrial automation and robotics",
      },

      {
        icon: <CloudIcon fontSize="large" />,
        title: "Cloud",
        description: "Cloud infrastructure and deployment",
      },

      {
        icon: <AnalyticsIcon fontSize="large" />,
        title: "Analytics",
        description: "Data analytics and business intelligence",
      },
    ],

    products: [
      {
        icon: <PrecisionManufacturingIcon fontSize="large" />,
        title: "eMachine",
        description: "Next generation machine monitoring platform",
        link: "/products/emachine",
      },
      {
        icon: <HubOutlinedIcon fontSize="large" />,
        title: "flowLink",
        description: "Industrial IOT gateway",
        link: "/products/flowlink",
      },
      {
        icon: <DirectionsRunOutlinedIcon fontSize="large" />,
        title: "Digital Gemba Walk",
        description: "Mobile-first shopfloor audit & observation system",
        link: "/products/gemba",
      },
    ],
  };
  const isSolidNavbar = !isHomePage || scrolled || activeMenu;
  return (
    <>
      {/* NAVBAR */}

      <AppBar
        position="fixed"
        elevation={isSolidNavbar || activeMenu ? 4 : 0}
        sx={{
          top: bannerVisible ? "48px" : "0px",
          backgroundColor:
            isSolidNavbar || activeMenu ? "white" : "transparent",
          color: isSolidNavbar || activeMenu ? "black" : "white",
          transition: "all .35s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: "space-between" }}>
            {/* LOGO */}

            <Box
              component={Link}
              to="/"
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src="/emachinelogo-horizontal.png"
                alt="IndSol"
                sx={{ height: 55, transform: "scale(3)" }}
              />
            </Box>

            {/* DESKTOP MENU */}

            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3 }}>
              {/* PRODUCTS */}

              <Box
                onMouseEnter={() => openMenu("products")}
                onMouseLeave={closeMenu}
                sx={{ position: "relative" }}
              >
                <Button
                  color="inherit"
                  endIcon={
                    <KeyboardArrowDownIcon
                      sx={{
                        transform:
                          activeMenu === "products"
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        transition: "transform .3s",
                      }}
                    />
                  }
                >
                  Products
                </Button>

                {/* Attach menu HERE instead of global */}
                {activeMenu === "products" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: "100vw",
                      background: "white",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                      zIndex: 1200,
                    }}
                  >
                    <FloatingMegaMenu items={menus["products"]} />
                  </Box>
                )}
              </Box>

              {/* SOLUTIONS */}

              <Box
                onMouseEnter={() => openMenu("solutions")}
                onMouseLeave={closeMenu}
              >
                <Button
                  color="inherit"
                  endIcon={
                    <KeyboardArrowDownIcon
                      sx={{
                        transform:
                          activeMenu === "solutions"
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        transition: "transform .3s",
                      }}
                    />
                  }
                >
                  Solutions
                </Button>
              </Box>

              {/* <Button sx={hoverStyle} color="inherit">
                Automations
              </Button>

              <Button sx={hoverStyle} color="inherit">
                Resources
              </Button> */}

              <Button
                component={Link}
                to="/pricing"
                sx={hoverStyle}
                color="inherit"
              >
                Pricing
              </Button>

              <Button
                component={Link}
                to="/contact"
                sx={hoverStyle}
                color="inherit"
              >
                Contact
              </Button>

              <Button
                component={Link}
                to="/book-demo"
                variant="contained"
                sx={{
                  ml: 2,
                  px: 3,
                  fontWeight: 700,
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #FFC400, #ffb300)",
                  color: "#000",
                  textDecoration: "none",
                  "&:hover": {
                    background: "linear-gradient(135deg, #ffb300, #ffa000)",
                  },
                }}
              >
                Book a demo
              </Button>
            </Box>

            {/* MOBILE MENU BUTTON */}

            <IconButton
              sx={{ display: { xs: "flex", md: "none" } }}
              color="inherit"
              onClick={toggleDrawer}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* BACKDROP */}

      <Box
        onClick={() => setActiveMenu(null)}
        sx={{
          position: "fixed",
          top: bannerVisible ? "112px" : "64px",
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15,23,42,0.25)",
          backdropFilter: "blur(4px)",
          opacity: activeMenu ? 1 : 0,
          pointerEvents: activeMenu ? "auto" : "none",
          transition: "opacity .35s",
          zIndex: 1100,
        }}
      />

      {/* MEGA MENU */}

      <AnimatePresence>
        {activeMenu && (
          <motion.div
            onMouseEnter={() => openMenu(activeMenu)}
            onMouseLeave={closeMenu}
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1],
            }}
            style={{
              position: "fixed",
              top: bannerVisible ? "112px" : "64px",
              left: 0,
              width: "100%",
              zIndex: 1200,
            }}
          >
            <Box
              sx={{
                width: "100%",
                background: "white",
                borderTop: "1px solid #eee",
                boxShadow:
                  "0 30px 60px rgba(50,50,93,.25), 0 15px 35px rgba(0,0,0,.15)",
              }}
            >
              <FloatingMegaMenu items={menus[activeMenu]} />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE DRAWER */}

      <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer}>
        <Box sx={{ width: 250 }}>
          <List>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/" onClick={toggleDrawer}>
                <ListItemText primary="Home" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton>
                <ListItemText primary="Products" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton>
                <ListItemText primary="Solutions" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}

const hoverStyle = {
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    width: "0%",
    height: "2px",
    bottom: 0,
    left: 0,
    backgroundColor: "currentColor",
    transition: "width .3s",
  },
  "&:hover::after": {
    width: "100%",
  },
};
